import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Star, Upload, Loader2, Send } from "lucide-react";
import { useLang } from "@/i18n";
import { compressImage } from "@/lib/image";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewModal({ onClose, onSubmitted }) {
  const { t } = useLang();
  const f = t.testimonials.form;
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setPhoto(compressed);
    } catch {
      toast.error(f.error);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || rating < 1 || text.trim().length < 3) {
      toast.error(f.errRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: name.trim(),
        location: location.trim() || null,
        rating,
        text: text.trim(),
        photo,
      });
      toast.success(f.success);
      onSubmitted?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : f.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#d4af37]/25 px-4 py-3 text-white placeholder-white/35 focus:border-[#d4af37] focus:outline-none transition-colors duration-300";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md overflow-y-auto"
      data-testid="review-modal"
    >
      <div
        className="min-h-screen flex items-start md:items-center justify-center px-5 py-12"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.form
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="w-full max-w-lg border border-[#d4af37]/25 bg-[#0a0a0a] p-8 md:p-10"
        >
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-serif-lux font-light text-3xl text-white">{f.title}</h3>
              <p className="text-white/50 text-sm mt-1">{f.subtitle}</p>
            </div>
            <button type="button" onClick={onClose} data-testid="review-close" className="text-white/50 hover:text-[#d4af37] transition-colors duration-300">
              <X size={22} />
            </button>
          </div>

          {/* Rating */}
          <div className="mt-7">
            <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{f.rating}</label>
            <div className="flex gap-1.5" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  data-testid={`review-star-${n}`}
                  className="transition-transform duration-200 hover:scale-110"
                  aria-label={`${n} stars`}
                >
                  <Star size={30} className={(hover || rating) >= n ? "text-[#d4af37]" : "text-white/20"} fill={(hover || rating) >= n ? "#d4af37" : "transparent"} />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{f.name} *</label>
              <input data-testid="review-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{f.location}</label>
              <input data-testid="review-location" className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{f.review} *</label>
            <textarea data-testid="review-text" rows={4} className={`${inputCls} resize-none`} value={text} onChange={(e) => setText(e.target.value)} placeholder={f.reviewPh} />
          </div>

          {/* Photo */}
          <div className="mt-5">
            <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{f.photo}</label>
            {photo ? (
              <div className="relative inline-block">
                <img src={photo} alt="preview" className="h-32 w-32 object-cover border border-[#d4af37]/30" />
                <button type="button" onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-black border border-[#d4af37]/40 text-[#d4af37] rounded-full p-1" data-testid="review-remove-photo">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-3 border border-dashed border-[#d4af37]/30 px-4 py-4 cursor-pointer hover:border-[#d4af37]/60 transition-colors duration-300" data-testid="review-photo-label">
                <Upload size={18} className="text-[#d4af37]" />
                <span className="text-white/60 text-sm">{f.photoHint}</span>
                <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="review-photo-input" />
              </label>
            )}
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              data-testid="review-submit"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-3 uppercase tracking-[0.15em] text-sm font-medium gold-glow disabled:opacity-60 hover:bg-[#f1e5ac] transition-colors duration-300"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> {f.submitting}</> : <>{f.submit} <Send size={15} /></>}
            </button>
            <button type="button" onClick={onClose} className="text-white/50 hover:text-white text-sm uppercase tracking-[0.12em] transition-colors duration-300">
              {f.cancel}
            </button>
          </div>
        </motion.form>
      </div>
    </motion.div>
  );
}
